import React, { FC, ReactElement, useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../config";
import { getThemeColors, getComponentColors, Theme } from "../theme/colors";

interface User {
  username: string;
  id: number;
  developer: number;
  schema_name: string;
  config_id: number;
  user_tables: string;
  ml_aurora: string;
}

interface RdmConfig {
  id: number;
  config_name: string;
  created_at: string;
  // Add other properties as needed
}

interface UserManagerProps {
  username: string;
  theme: Theme;
  devNumber?: number;
}

interface ApiErrorResponse {
  error?: string;
}

type SortField = keyof User;
type SortDirection = "asc" | "desc";

interface Filters {
  id: string;
  username: string;
  schema_name: string;
  config_id: string;
  user_tables: string;
  ml_aurora: string;
  developer: string;
}

const UserManager: FC<UserManagerProps> = ({
  username,
  theme,
  devNumber,
}): ReactElement => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingUsers, setUpdatingUsers] = useState<Set<number>>(new Set());
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null);
  const [editingConfigValue, setEditingConfigValue] = useState<string>("");
  const [updatingConfigIds, setUpdatingConfigIds] = useState<Set<number>>(
    new Set()
  );
  const [sortField, setSortField] = useState<SortField>("username");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filters, setFilters] = useState<Filters>({
    id: "",
    username: "",
    schema_name: "",
    config_id: "",
    user_tables: "",
    ml_aurora: "",
    developer: "",
  });
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [rdmConfigs, setRdmConfigs] = useState<RdmConfig[]>([]);
  const [rdmLoading, setRdmLoading] = useState<boolean>(false);
  const [rdmError, setRdmError] = useState<string | null>(null);
  const [assigningUsers, setAssigningUsers] = useState<Set<number>>(new Set());

  const colors = getThemeColors(username, theme);
  const componentColors = getComponentColors("table", theme);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.post(`${config.api.baseUrl}/chat/users`, {
          username,
        });

        setUsers(response.data.users || []);
      } catch (error: unknown) {
        console.error("Error fetching users:", error);
        if (axios.isAxiosError(error) && error.response?.data) {
          setError(
            (error.response.data as ApiErrorResponse)?.error ||
              "Failed to fetch users"
          );
        } else {
          setError("Failed to fetch users");
        }
      } finally {
        setIsLoading(false);
      }
    };

    const fetchRdmConfigs = async () => {
      setRdmLoading(true);
      setRdmError(null);

      try {
        const response = await axios.post(`${config.api.baseUrl}/chat/rdm`, {
          username,
        });
        setRdmConfigs(response.data?.rdm ?? []);
      } catch (error: unknown) {
        console.error("Error fetching RDM configs:", error);
        if (axios.isAxiosError(error) && error.response?.data) {
          setRdmError(
            (error.response.data as ApiErrorResponse)?.error ||
              "Failed to fetch RDM configs"
          );
        } else {
          setRdmError("Failed to fetch RDM configs");
        }
      } finally {
        setRdmLoading(false);
      }
    };

    fetchUsers();
    fetchRdmConfigs();
  }, [username]);

  const handleDevModeToggle = async (user: User) => {
    setUpdatingUsers((prev) => new Set(prev).add(user.id));

    try {
      await axios.post(`${config.api.baseUrl}/chat/user-update`, {
        editor: username,
        edit_user: user.username,
        user_id: user.id,
        developer: user.developer,
      });

      // Update the local state to reflect the change
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, developer: u.developer === 1 ? 0 : 1 } : u
        )
      );
    } catch (error: unknown) {
      console.error("Error updating user:", error);
      if (axios.isAxiosError(error) && error.response?.data) {
        setError(
          (error.response.data as ApiErrorResponse)?.error ||
            "Failed to update user"
        );
      } else {
        setError("Failed to update user");
      }
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };

  const handleConfigIdEdit = (user: User) => {
    setEditingConfigId(user.id);
    setEditingConfigValue(user.config_id.toString());
  };

  const handleConfigIdUpdate = async (user: User) => {
    const newConfigId = parseInt(editingConfigValue, 10);

    // Validate input
    if (isNaN(newConfigId) || newConfigId < 0) {
      setError("Config ID must be a valid positive number");
      return;
    }

    // If value hasn't changed, just exit edit mode
    if (newConfigId === user.config_id) {
      setEditingConfigId(null);
      setEditingConfigValue("");
      return;
    }

    setUpdatingConfigIds((prev) => new Set(prev).add(user.id));

    try {
      await axios.post(`${config.api.baseUrl}/chat/config-update`, {
        editor: username,
        edit_user: user.username,
        user_id: user.id,
        config_id: newConfigId,
      });

      // Update the local state to reflect the change
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, config_id: newConfigId } : u
        )
      );

      setEditingConfigId(null);
      setEditingConfigValue("");
    } catch (error: unknown) {
      console.error("Error updating config ID:", error);
      if (axios.isAxiosError(error) && error.response?.data) {
        setError(
          (error.response.data as ApiErrorResponse)?.error ||
            "Failed to update config ID"
        );
      } else {
        setError("Failed to update config ID");
      }
    } finally {
      setUpdatingConfigIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };

  const handleConfigIdCancel = () => {
    setEditingConfigId(null);
    setEditingConfigValue("");
  };

  const handleConfigIdKeyPress = (e: React.KeyboardEvent, user: User) => {
    if (e.key === "Enter") {
      handleConfigIdUpdate(user);
    } else if (e.key === "Escape") {
      handleConfigIdCancel();
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUserSelect = (userId: number) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredAndSortedUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredAndSortedUsers.map((user) => user.id)));
    }
  };

  const handleAssignLatest = async () => {
    if (selectedUsers.size === 0 || rdmConfigs.length === 0) return;

    const latestConfigId = rdmConfigs[0].id;
    setAssigningUsers(new Set(selectedUsers));

    try {
      for (const userId of Array.from(selectedUsers)) {
        const user = users.find((u) => u.id === userId);
        if (user) {
          await axios.post(`${config.api.baseUrl}/chat/config-update`, {
            editor: username,
            edit_user: user.username,
            user_id: user.id,
            config_id: latestConfigId,
          });
        }
      }

      // Update the local state to reflect the changes
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          selectedUsers.has(u.id) ? { ...u, config_id: latestConfigId } : u
        )
      );

      // Clear selection after successful assignment
      setSelectedUsers(new Set());
    } catch (error: unknown) {
      console.error("Error assigning latest config:", error);
      if (axios.isAxiosError(error) && error.response?.data) {
        setError(
          (error.response.data as ApiErrorResponse)?.error ||
            "Failed to assign latest config"
        );
      } else {
        setError("Failed to assign latest config");
      }
    } finally {
      setAssigningUsers(new Set());
    }
  };

  const handleAssignSpecific = async () => {
    if (selectedUsers.size === 0) return;

    // For now, we'll use a simple prompt. In a real app, you might want a modal or dropdown
    const configIdStr = prompt("Enter the specific config ID to assign:");
    if (!configIdStr) return;

    const configId = parseInt(configIdStr, 10);
    if (isNaN(configId) || configId < 0) {
      setError("Config ID must be a valid positive number");
      return;
    }

    setAssigningUsers(new Set(selectedUsers));

    try {
      for (const userId of Array.from(selectedUsers)) {
        const user = users.find((u) => u.id === userId);
        if (user) {
          await axios.post(`${config.api.baseUrl}/chat/config-update`, {
            editor: username,
            edit_user: user.username,
            user_id: user.id,
            config_id: configId,
          });
        }
      }

      // Update the local state to reflect the changes
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          selectedUsers.has(u.id) ? { ...u, config_id: configId } : u
        )
      );

      // Clear selection after successful assignment
      setSelectedUsers(new Set());
    } catch (error: unknown) {
      console.error("Error assigning specific config:", error);
      if (axios.isAxiosError(error) && error.response?.data) {
        setError(
          (error.response.data as ApiErrorResponse)?.error ||
            "Failed to assign specific config"
        );
      } else {
        setError("Failed to assign specific config");
      }
    } finally {
      setAssigningUsers(new Set());
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      return (
        user.id.toString().includes(filters.id) &&
        user.username.toLowerCase().includes(filters.username.toLowerCase()) &&
        user.schema_name
          .toLowerCase()
          .includes(filters.schema_name.toLowerCase()) &&
        user.config_id.toString().includes(filters.config_id) &&
        user.user_tables
          .toLowerCase()
          .includes(filters.user_tables.toLowerCase()) &&
        user.ml_aurora
          .toLowerCase()
          .includes(filters.ml_aurora.toLowerCase()) &&
        (filters.developer === "" ||
          user.developer.toString() === filters.developer)
      );
    });

    return filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === "asc" ? comparison : -comparison;
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        const comparison = aValue - bValue;
        return sortDirection === "asc" ? comparison : -comparison;
      }

      return 0;
    });
  }, [users, filters, sortField, sortDirection]);

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: componentColors.background || colors.background,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: colors.optionsBg,
    color: colors.text,
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "bold",
    borderBottom: `1px solid ${colors.border}`,
    cursor: "pointer",
    userSelect: "none",
    position: "relative",
  };

  const cellStyle: React.CSSProperties = {
    padding: "12px 16px",
    borderBottom: `1px solid ${colors.border}`,
  };

  const rowStyle: React.CSSProperties = {
    cursor: "pointer",
  };

  const checkboxStyle: React.CSSProperties = {
    transform: "scale(1.2)",
    accentColor: colors.buttonHover,
    cursor: "pointer",
  };

  const filterInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "4px 8px",
    border: `1px solid ${colors.border}`,
    borderRadius: "4px",
    backgroundColor: colors.background,
    color: colors.text,
    fontSize: "12px",
  };

  const filterRowStyle: React.CSSProperties = {
    backgroundColor: colors.convoStarterBg,
  };

  const buttonStyle: React.CSSProperties = {
    padding: "8px 16px",
    margin: "4px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
  };

  const assignLatestButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "#007bff",
    color: "#ffffff",
  };

  const assignSpecificButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "#6c757d",
    color: "#ffffff",
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginBottom: "20px",
    padding: "12px",
    backgroundColor: componentColors.background || colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    minHeight: "50px",
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return " ↕";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  return (
    <div
      style={{
        padding: "20px",
        height: "calc(100vh - 48px)",
        backgroundColor: componentColors.background || colors.background,
        color: colors.text,
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          marginBottom: "20px",
          color: colors.text,
          fontSize: "24px",
          fontWeight: "bold",
        }}
      >
        User Manager
      </h1>

      {/* RDM Config Display */}
      <div
        style={{
          marginBottom: "20px",
          padding: "16px",
          backgroundColor: componentColors.background || colors.background,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
        }}
      >
        <h3
          style={{
            marginBottom: "12px",
            marginTop: "0px",
            color: colors.text,
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          Latest Configuration
        </h3>
        {rdmLoading && (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <div>Loading RDM configs...</div>
          </div>
        )}
        {rdmError && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              marginBottom: "10px",
            }}
          >
            Error: {rdmError}
          </div>
        )}
        {!rdmLoading && !rdmError && rdmConfigs.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "200px 1fr",
              gap: "12px",
              padding: "8px 0",
            }}
          >
            <div style={{ fontWeight: 600 }}>Config ID:</div>
            <div>{rdmConfigs[0].id}</div>
            <div style={{ fontWeight: 600 }}>Config Name:</div>
            <div>{rdmConfigs[0].config_name}</div>
          </div>
        )}
        {!rdmLoading && !rdmError && rdmConfigs.length === 0 && (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <div>No RDM configurations found.</div>
          </div>
        )}
      </div>

      {/* Assignment Buttons - Only show when users are selected and devNumber is 99 */}
      {devNumber === 99 && selectedUsers.size > 0 && (
        <div style={buttonContainerStyle}>
          <div
            style={{
              marginRight: "auto",
              fontWeight: "500",
              color: colors.text,
            }}
          >
            {selectedUsers.size} user{selectedUsers.size !== 1 ? "s" : ""}{" "}
            selected
          </div>
          <button
            onClick={handleAssignLatest}
            disabled={rdmConfigs.length === 0 || assigningUsers.size > 0}
            style={{
              ...assignLatestButtonStyle,
              opacity:
                rdmConfigs.length === 0 || assigningUsers.size > 0 ? 0.6 : 1,
              cursor:
                rdmConfigs.length === 0 || assigningUsers.size > 0
                  ? "not-allowed"
                  : "pointer",
            }}
            onMouseEnter={(e) => {
              if (rdmConfigs.length > 0 && assigningUsers.size === 0) {
                e.currentTarget.style.backgroundColor = "#0056b3";
              }
            }}
            onMouseLeave={(e) => {
              if (rdmConfigs.length > 0 && assigningUsers.size === 0) {
                e.currentTarget.style.backgroundColor = "#007bff";
              }
            }}
          >
            {assigningUsers.size > 0 ? "Assigning..." : "Assign Latest Config"}
          </button>
          <button
            onClick={handleAssignSpecific}
            disabled={assigningUsers.size > 0}
            style={{
              ...assignSpecificButtonStyle,
              opacity: assigningUsers.size > 0 ? 0.6 : 1,
              cursor: assigningUsers.size > 0 ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (assigningUsers.size === 0) {
                e.currentTarget.style.backgroundColor = "#5a6268";
              }
            }}
            onMouseLeave={(e) => {
              if (assigningUsers.size === 0) {
                e.currentTarget.style.backgroundColor = "#6c757d";
              }
            }}
          >
            {assigningUsers.size > 0
              ? "Assigning..."
              : "Assign Specific Config"}
          </button>
        </div>
      )}

      {isLoading && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div>Loading users...</div>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          Error: {error}
        </div>
      )}

      {!isLoading && !error && users.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {devNumber === 99 && <th style={headerStyle}>Select</th>}
                <th style={headerStyle} onClick={() => handleSort("id")}>
                  ID{getSortIcon("id")}
                </th>
                <th style={headerStyle} onClick={() => handleSort("username")}>
                  Username{getSortIcon("username")}
                </th>
                {devNumber !== 99 && (
                  <th
                    style={headerStyle}
                    onClick={() => handleSort("schema_name")}
                  >
                    Schema Name{getSortIcon("schema_name")}
                  </th>
                )}
                <th style={headerStyle} onClick={() => handleSort("config_id")}>
                  Config ID{getSortIcon("config_id")}
                </th>
                {devNumber !== 99 && (
                  <>
                    {/* <th
                      style={headerStyle}
                      onClick={() => handleSort("user_tables")}
                    >
                      User Tables{getSortIcon("user_tables")}
                    </th> */}
                    <th
                      style={headerStyle}
                      onClick={() => handleSort("ml_aurora")}
                    >
                      ML Aurora{getSortIcon("ml_aurora")}
                    </th>
                    <th
                      style={headerStyle}
                      onClick={() => handleSort("developer")}
                    >
                      Dev Mode{getSortIcon("developer")}
                    </th>
                  </>
                )}
              </tr>
              <tr style={filterRowStyle}>
                {devNumber === 99 && (
                  <td style={cellStyle}>
                    <input
                      type="checkbox"
                      checked={
                        selectedUsers.size === filteredAndSortedUsers.length &&
                        filteredAndSortedUsers.length > 0
                      }
                      onChange={handleSelectAll}
                      style={checkboxStyle}
                    />
                  </td>
                )}
                <td style={cellStyle}>
                  <input
                    type="text"
                    placeholder="Filter ID..."
                    value={filters.id}
                    onChange={(e) => handleFilterChange("id", e.target.value)}
                    style={filterInputStyle}
                  />
                </td>
                <td style={cellStyle}>
                  <input
                    type="text"
                    placeholder="Filter username..."
                    value={filters.username}
                    onChange={(e) =>
                      handleFilterChange("username", e.target.value)
                    }
                    style={filterInputStyle}
                  />
                </td>
                {devNumber !== 99 && (
                  <td style={cellStyle}>
                    <input
                      type="text"
                      placeholder="Filter schema..."
                      value={filters.schema_name}
                      onChange={(e) =>
                        handleFilterChange("schema_name", e.target.value)
                      }
                      style={filterInputStyle}
                    />
                  </td>
                )}
                <td style={cellStyle}>
                  <input
                    type="text"
                    placeholder="Filter config..."
                    value={filters.config_id}
                    onChange={(e) =>
                      handleFilterChange("config_id", e.target.value)
                    }
                    style={filterInputStyle}
                  />
                </td>
                {devNumber !== 99 && (
                  <>
                    {/* <td style={cellStyle}>
                      <input
                        type="text"
                        placeholder="Filter tables..."
                        value={filters.user_tables}
                        onChange={(e) =>
                          handleFilterChange("user_tables", e.target.value)
                        }
                        style={filterInputStyle}
                      />
                    </td> */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        placeholder="Filter ML Aurora..."
                        value={filters.ml_aurora}
                        onChange={(e) =>
                          handleFilterChange("ml_aurora", e.target.value)
                        }
                        style={filterInputStyle}
                      />
                    </td>
                    <td style={cellStyle}>
                      <select
                        value={filters.developer}
                        onChange={(e) =>
                          handleFilterChange("developer", e.target.value)
                        }
                        style={filterInputStyle}
                      >
                        <option value="">All</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                    </td>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedUsers.map((user) => (
                <tr
                  key={user.id}
                  style={rowStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      colors.convoStarterHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {devNumber === 99 && (
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => handleUserSelect(user.id)}
                        style={checkboxStyle}
                      />
                    </td>
                  )}
                  <td style={cellStyle}>{user.id}</td>
                  <td style={cellStyle}>{user.username}</td>
                  {devNumber !== 99 && (
                    <td style={cellStyle}>{user.schema_name}</td>
                  )}
                  <td
                    style={{
                      ...cellStyle,
                      cursor: devNumber === 99 ? "default" : "pointer",
                      position: "relative",
                    }}
                    onDoubleClick={
                      devNumber === 99
                        ? undefined
                        : () => handleConfigIdEdit(user)
                    }
                  >
                    {editingConfigId === user.id && devNumber !== 99 ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="number"
                          value={editingConfigValue}
                          onChange={(e) =>
                            setEditingConfigValue(e.target.value)
                          }
                          onKeyDown={(e) => handleConfigIdKeyPress(e, user)}
                          onBlur={() => handleConfigIdUpdate(user)}
                          autoFocus
                          style={{
                            ...filterInputStyle,
                            width: "80px",
                            minWidth: "60px",
                          }}
                          disabled={updatingConfigIds.has(user.id)}
                        />
                        {updatingConfigIds.has(user.id) && (
                          <span
                            style={{ fontSize: "12px", color: colors.text }}
                          >
                            Updating...
                          </span>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span>{user.config_id}</span>
                        {updatingConfigIds.has(user.id) && devNumber !== 99 && (
                          <span
                            style={{ fontSize: "12px", color: colors.text }}
                          >
                            Updating...
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  {devNumber !== 99 && (
                    <>
                      {/* <td style={cellStyle}>{user.user_tables}</td> */}
                      <td style={cellStyle}>{user.ml_aurora}</td>
                      <td style={cellStyle}>
                        <input
                          type="checkbox"
                          checked={user.developer === 1}
                          onChange={() => handleDevModeToggle(user)}
                          disabled={updatingUsers.has(user.id)}
                          style={{
                            ...checkboxStyle,
                            opacity: updatingUsers.has(user.id) ? 0.5 : 1,
                          }}
                        />
                        {updatingUsers.has(user.id) && (
                          <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                            Updating...
                          </span>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading &&
        !error &&
        filteredAndSortedUsers.length === 0 &&
        users.length > 0 && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div>No users match the current filters.</div>
          </div>
        )}

      {!isLoading && !error && users.length === 0 && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div>No users found.</div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
